import React from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const zebraStripeAnimation = keyframes`
  0% { background-position: 100% 0%; }
  100% { background-position: 0% 0%; }
`;

const NRAIThinkingButton = ({
  isThinking,
  label = 'Show Thinking',
  thinkingLabel = 'Thinking...',
  isExpanded = false,
  onToggle,
  color,
  showChevron = true,
  asText = false,
  sx,
  ariaLabel,
  ariaControls,
}) => {
  const theme = useTheme();
  const displayLabel = isThinking ? thinkingLabel : label;
  // Use theme text.secondary as default color, or use provided color
  const textColor = color || theme.palette.text.secondary;
  // Get semi-transparent color from theme
  const semiTransparent = theme.palette.text.semiTransparent;

  const textContent = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Container with relative positioning for crossfade */}
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          {/* Thinking label - visible when thinking */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              whiteSpace: 'nowrap',
              background: `linear-gradient(
                60deg,
                ${textColor} 0%,
                ${textColor} 25%,
                ${semiTransparent} 25%,
                ${semiTransparent} 50%,
                ${textColor} 50%,
                ${textColor} 75%,
                ${semiTransparent} 75%
              )`,
              backgroundSize: '200% 100%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              animation: isThinking
                ? `${zebraStripeAnimation} 1000ms linear infinite`
                : 'none',
              opacity: isThinking ? 1 : 0,
              transition: 'opacity 300ms ease-in-out',
              pointerEvents: isThinking ? 'auto' : 'none',
              position: 'absolute',
              top: 0,
              left: 0,
              visibility: isThinking ? 'visible' : 'hidden',
            }}
          >
            {thinkingLabel}
          </Typography>

          {/* Normal label - visible when not thinking */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              whiteSpace: 'nowrap',
              color: textColor,
              opacity: isThinking ? 0 : 1,
              transition: 'opacity 300ms ease-in-out',
              pointerEvents: isThinking ? 'none' : 'auto',
              position: 'absolute',
              top: 0,
              left: 0,
              visibility: isThinking ? 'hidden' : 'visible',
            }}
          >
            {label}
          </Typography>

          {/* Invisible spacer to maintain layout - renders the longer of the two labels */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              whiteSpace: 'nowrap',
              visibility: 'hidden',
              pointerEvents: 'none',
            }}
          >
            {thinkingLabel.length > label.length ? thinkingLabel : label}
          </Typography>
        </Box>
        {showChevron && (
          <ExpandMoreIcon
            sx={{
              width: 20,
              height: 20,
              flexShrink: 0,
              color: textColor,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 200ms ease-in-out',
            }}
          />
        )}
      </Box>
  );

  // If asText is true, render as a Box with text cursor instead of Button
  if (asText) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'text',
          ...sx,
        }}
      >
        {textContent}
      </Box>
    );
  }

  // Default: render as Button
  return (
    <Button
      variant="text"
      onClick={onToggle}
      disableRipple
      aria-label={ariaLabel || displayLabel}
      aria-expanded={isExpanded}
      aria-controls={ariaControls}
      sx={{
        textTransform: 'none',
        padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
        minWidth: 'auto',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        '&:active': {
          backgroundColor: theme.palette.action.selected,
        },
        ...sx,
      }}
    >
      {textContent}
    </Button>
  );
};

export default NRAIThinkingButton;
